import { FC, useCallback } from 'react'

import * as SC from '../styles'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Form, useFormData } from 'brief-form'
import { BsaPeriodEditRequest } from 'commons/types/DTO/bank/bsa'
import { Request } from 'commons/utils/request'
import { DatePicker, Dialog, DialogFace, DialogFooterActions, Toast, Validators } from 'ui/components'
import { useConfirmation } from 'ui/components/Dialog/ConfirmationDialogV2'

import { handlerServerError } from '@orbit/errors'

import { EditPeriodDialogProps } from './EditPeriodDialog.types'

export const EditPeriodDialog: FC<EditPeriodDialogProps> = (props) => {
  const { id, initialValue, onClose } = props
  const { config, Field, validate } = useFormData<Omit<BsaPeriodEditRequest, 'id'>>({
    initialValue
  })

  const queryClient = useQueryClient()
  const confirmation = useConfirmation()

  const { mutateAsync, isPending } = useMutation<void, Error, BsaPeriodEditRequest>({
    mutationFn: (opts) => Request.put(Request.urls.bank.editSarPeriod, opts),
    onError: (error) => handlerServerError(error, { form: config }),
    onSuccess: () => {
      Toast.successSmall(`SAR period was successfully changed.`)
      queryClient.invalidateQueries({ queryKey: ['sar-details'] })
      onClose()
    }
  })

  const originalChangeHandler = config.onChange
  config.onChange = useCallback(
    (v, e) => {
      // clear errors on change, but make sure this change do not called by server errors mapping func
      if (config.errors.startDate && e.startDate) {
        delete e.startDate
      }

      // clear errors on change, but make sure this change do not called by server errors mapping func
      if (config.errors.endDate && e.endDate) {
        delete e.endDate
      }

      originalChangeHandler(v, e)
    },
    [originalChangeHandler, config.errors.startDate, config.errors.endDate]
  )

  const startEditing = async () => {
    await confirmation.showAskConfirmation({
      message: 'The SAR will be recalculated based on the chosen period. Do you wish to continue?',
      submitButtonText: 'Continue',
      confirmCallback: async () => {
        mutateAsync({ ...config.value, id })
      }
    })
  }

  return (
    <Dialog
      id="edit-sar-period"
      title="Edit SAR Period"
      face={DialogFace.PRIMARY}
      onClose={onClose}
      onSubmit={() => {
        if (validate({ updateFields: true }).valid) startEditing()
      }}
      isSubmitting={isPending}
      footerActions={[DialogFooterActions.SAVE, DialogFooterActions.CANCEL]}
    >
      <SC.EditPeriodDialogInner>
        <Form config={config} disabled={isPending}>
          <Field
            name="startDate"
            label="Transactions Start Date"
            input={DatePicker}
            validator={Validators.required.field}
            inputProps={{
              'data-qa': 'Start Date'
            }}
          />
          <Field
            name="endDate"
            label="Transactions End Date"
            input={DatePicker}
            validator={Validators.required.field}
            inputProps={{
              'data-qa': 'End Date'
            }}
          />
        </Form>
      </SC.EditPeriodDialogInner>
    </Dialog>
  )
}
